import type { EvalSectionData, PocSectionData, QprSectionData } from '../types';
import type { SoapSectionData } from '../components/clinical/ManualSoapWorkspace';
import { isCurrentCanonicalGoalStatus } from './canonicalGoalDirect';

export const DEFAULT_SKILLED_NECESSITY =
  'Skilled speech-language pathology intervention is medically necessary to remediate expressive, receptive, and articulation deficits impacting daily functional communication.';

export interface AuthoringClientChart {
  primary_language?: string | null;
  physician_name?: string | null;
  physician_npi?: string | null;
  physician_fax?: string | null;
  physician_phone?: string | null;
  physician_practice?: string | null;
  guardian_name?: string | null;
  guardian_email?: string | null;
  guardian_relationship?: string | null;
}

export interface AuthoringEncounterSeed {
  dos?: string;
  timeIn?: string;
  serviceCode?: string;
}

export interface ClientCanonicalGoal {
  id: string;
  description: string;
  baseline_performance?: string;
  target_percentage?: number;
  current_percentage?: number;
  status?: string;
}

function chartText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function isoDate(now: Date): string {
  return now.toISOString().slice(0, 10);
}

function oneYearLater(now: Date): string {
  const next = new Date(now);
  next.setFullYear(next.getFullYear() + 1);
  return isoDate(next);
}

function physicianFromChart(client: AuthoringClientChart | null | undefined) {
  return {
    physician_name: chartText(client?.physician_name),
    physician_npi: chartText(client?.physician_npi),
    physician_fax: chartText(client?.physician_fax),
    physician_phone: chartText(client?.physician_phone),
    physician_practice: chartText(client?.physician_practice)
  };
}

export function createInitialEvalAuthoring(now = new Date()): EvalSectionData {
  return {
    client_context: '',
    primary_language: 'English',
    safety_precautions: '',
    caregiver_concerns: '',
    direct_observations: '',
    medical_history: '',
    receptive_language: '',
    expressive_language: '',
    speech_production: '',
    pragmatic_social: '',
    standardized_tests: [],
    diagnostic_impressions: '',
    medical_necessity_rationale: '',
    established_goals: [],
    prescribed_frequency: '2 sessions per week',
    prescribed_duration: '60 minutes per session',
    cpt_code: '92507',
    eval_cpt_code: '92523',
    evaluation_date: isoDate(now),
    treatment_setting: '',
    poc_start_date: isoDate(now),
    poc_end_date: oneYearLater(now),
    home_program_strategies: '',
    caregiver_training_plan: '',
    prognosis: 'Good',
    prognostic_indicators: '',
    discharge_criteria: '',
    physician_name: '',
    physician_npi: '',
    physician_fax: '',
    physician_phone: ''
  };
}

export function createInitialPocAuthoring(now = new Date()): PocSectionData {
  return {
    poc_start_date: isoDate(now),
    poc_end_date: oneYearLater(now),
    initial_eval_date: '',
    progress_summary: '',
    caregiver_participation: '',
    continued_skilled_need: '',
    medical_necessity_statement: '',
    goals: [],
    frequency: '2 sessions per week',
    duration: '60 minutes per session',
    setting: 'Home / Natural Community Setting',
    home_program_carryover: '',
    prognosis: 'Good',
    discharge_plan: '',
    physician_name: '',
    physician_npi: '',
    physician_fax: '',
    physician_phone: ''
  };
}

export function createInitialQprAuthoring(): QprSectionData {
  return {
    reporting_period_label: 'Q1 2026',
    reporting_period_start: '2026-01-01',
    reporting_period_end: '2026-03-31',
    attendance: {
      completed_sessions: 0,
      cancelled_sessions: 0,
      no_show_sessions: 0,
      cancellation_reasons: '',
      service_interruptions: ''
    },
    collaboration_summary: '',
    medical_updates: '',
    therapy_summary: '',
    skilled_care_justification: '',
    goals_review: [],
    home_program_strategies: '',
    home_program_oversight: {
      clinician_oversight_notes: ''
    },
    home_program_changes: '',
    prognostic_indicators: '',
    recommended_frequency: '',
    frequency_rationale: '',
    physician_name: '',
    physician_npi: ''
  };
}

export function createInitialSoapAuthoring(encounter: AuthoringEncounterSeed = {}, now = new Date()): SoapSectionData {
  return {
    dos: encounter.dos || isoDate(now),
    time_in: encounter.timeIn || '',
    time_out: '',
    duration_minutes: 0,
    service_code: encounter.serviceCode || '',
    clients_treated_together: '',
    clinician_evidence: '',
    session_narrative: '',
    targeted_goals: {},
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    medical_necessity: DEFAULT_SKILLED_NECESSITY,
    guardian_name: '',
    guardian_email: '',
    guardian_relationship: 'Parent / Legal Guardian',
    attestation_checked: false
  };
}

export function withClientChartOnEval(base: EvalSectionData, client: AuthoringClientChart | null | undefined): EvalSectionData {
  return {
    ...base,
    primary_language: chartText(client?.primary_language) || 'English',
    ...physicianFromChart(client)
  };
}

export function withClientChartOnPoc(base: PocSectionData, client: AuthoringClientChart | null | undefined): PocSectionData {
  return {
    ...base,
    ...physicianFromChart(client)
  };
}

export function withClientChartOnQpr(base: QprSectionData, client: AuthoringClientChart | null | undefined): QprSectionData {
  const physician = physicianFromChart(client);
  return {
    ...base,
    physician_name: physician.physician_name,
    physician_npi: physician.physician_npi
  };
}

export function withClientChartOnSoap(base: SoapSectionData, client: AuthoringClientChart | null | undefined): SoapSectionData {
  return {
    ...base,
    guardian_name: chartText(client?.guardian_name),
    guardian_email: chartText(client?.guardian_email),
    guardian_relationship: chartText(client?.guardian_relationship) || 'Parent / Legal Guardian'
  };
}

export function authoringForSelectedClient(
  client: AuthoringClientChart | null | undefined,
  encounter: AuthoringEncounterSeed = {},
  now = new Date()
) {
  return {
    evalData: withClientChartOnEval(createInitialEvalAuthoring(now), client),
    pocData: withClientChartOnPoc(createInitialPocAuthoring(now), client),
    qprData: withClientChartOnQpr(createInitialQprAuthoring(), client),
    soapData: withClientChartOnSoap(createInitialSoapAuthoring(encounter, now), client)
  };
}

export function currentCanonicalGoals<T extends { status?: string | null }>(goals: T[]): T[] {
  return goals.filter((goal) => isCurrentCanonicalGoalStatus(goal.status));
}

export function pocGoalsFromClientGoals(goals: ClientCanonicalGoal[]): NonNullable<PocSectionData['goals']> {
  return currentCanonicalGoals(goals).map((goal) => ({
    id: goal.id,
    description: goal.description,
    baseline: goal.baseline_performance || 'On file',
    target_percentage: goal.target_percentage || 80,
    current_percentage: goal.current_percentage || 0,
    status: goal.status || 'IN_PROGRESS'
  }));
}

function recordedText(value: unknown): string | undefined {
  const text = typeof value === 'string' ? value.trim() : '';
  return text || undefined;
}

function recordedNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

export function qprReviewsFromClientGoals(goals: ClientCanonicalGoal[]): NonNullable<QprSectionData['goals_review']> {
  return currentCanonicalGoals(goals).map((goal) => ({
    goal_id: goal.id,
    description: goal.description,
    baseline: recordedText(goal.baseline_performance),
    target_percentage: recordedNumber(goal.target_percentage),
    current_accuracy: recordedNumber(goal.current_percentage)
  }));
}

export function qprGoalReviewPresentation(item: {
  change_status?: string | null;
  recommendation?: string | null;
  baseline?: string | null;
  target_percentage?: number | null;
  current_accuracy?: number | null;
  cueing_level?: string | null;
}) {
  return {
    changeStatus: recordedText(item.change_status) || 'Not assessed',
    recommendation: recordedText(item.recommendation) || 'Not assessed',
    baseline: recordedText(item.baseline) || 'Not documented',
    target: recordedNumber(item.target_percentage) !== undefined ? `${item.target_percentage}%` : 'Not documented',
    currentAccuracy: recordedNumber(item.current_accuracy) !== undefined ? `${item.current_accuracy}%` : 'Not documented',
    cueing: recordedText(item.cueing_level) || 'Not documented'
  };
}

export function qprAbsentText(value: unknown): string {
  return recordedText(value) || 'Not documented';
}

export function soapTargetsFromClientGoals(goals: ClientCanonicalGoal[]): SoapSectionData['targeted_goals'] {
  const targeted: SoapSectionData['targeted_goals'] = {};
  currentCanonicalGoals(goals).forEach((goal, index) => {
    targeted[goal.id] = {
      targeted: index === 0,
      accuracy: goal.target_percentage || 80,
      cueing: 'Mod Visual',
      evidenceNote: ''
    };
  });
  return targeted;
}

export function canonicalGoalsFromClientDetail(goals: unknown): ClientCanonicalGoal[] {
  if (!Array.isArray(goals)) return [];
  return goals.filter((goal): goal is ClientCanonicalGoal => {
    return Boolean(goal) && typeof goal === 'object' && typeof (goal as ClientCanonicalGoal).id === 'string';
  });
}

export function clientLoadIsCurrent(requestToken: number, activeToken: number): boolean {
  return requestToken === activeToken;
}
